'use client';

import { useMemo, useRef } from 'react';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { useHasOverflow } from '@/app/(main)/_components/patient-selection/_hooks/use-has-overflow.hook';
import { PatientSelectionModalKeys } from '@/app/(main)/_components/patient-selection/_types/patient-selection-modal-keys.enum';
import { formatAgeRange } from '@/app/(main)/_components/patient-selection/_utils/patient-age-range.util';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { cn } from '@/utils/cn-tw-merge';
import { formatDocumentNumber } from '@/utils/formatters/format-document-number';
import { buildOutOfAgeRangeMessage } from '@/utils/patient/build-out-of-age-range-message.util';
import { PatientOption, ScheduleAgeRange } from '@/types/patient-selection.types';

export interface PatientSelectionListProps {
  patients: PatientOption[];
  ageRange: ScheduleAgeRange;
  requiresPhoneConfirmation: boolean;
  canSearchAnotherOption: boolean;
  onSelectPatient: (patient: PatientOption) => void;
  onRequestPhoneConfirmation: (patient: PatientOption) => void;
  onSearchAnotherOption: () => void;
}

const firstNameOf = ({ name }: PatientOption): string => name.split(' ')[0];

export function PatientSelectionList({
  patients,
  ageRange,
  requiresPhoneConfirmation,
  canSearchAnotherOption,
  onSelectPatient,
  onRequestPhoneConfirmation,
  onSearchAnotherOption,
}: PatientSelectionListProps) {
  const { handleJump, onClose } = useProgressiveModal<PatientSelectionModalKeys>();
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const outOfRangeNames = useMemo(
    () => patients.filter((patient) => !patient.isWithinAgeRange).map(firstNameOf),
    [patients],
  );

  const warningMessage = buildOutOfAgeRangeMessage(outOfRangeNames, formatAgeRange(ageRange));
  const offersAnotherOption = canSearchAnotherOption && outOfRangeNames.length > 0;
  const listScrolls = useHasOverflow(scrollAreaRef, [patients, offersAnotherOption]);

  const handleSelectPatient = (patient: PatientOption) => {
    if (requiresPhoneConfirmation) {
      onRequestPhoneConfirmation(patient);
      handleJump({ key: PatientSelectionModalKeys.PHONE_CONFIRMATION });
      return;
    }

    onSelectPatient(patient);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-col gap-5 px-5 pb-5 pt-5">
        <div className="flex items-start justify-between gap-2">
          <Typography
            variant={TypographyVariant.MODAL_TITLE}
            data-testid={DataTestIdEnum.PATIENT_SELECTION_LIST_TITLE}
          >
            Escolha o paciente
          </Typography>
          <ModalCloseButton
            label="Fechar modal"
            data-testid={DataTestIdEnum.PATIENT_SELECTION_LIST_CLOSE_BUTTON}
            className="shrink-0"
            onClick={onClose}
          />
        </div>

        <Typography
          variant={TypographyVariant.BODY_TEXT}
          className="text-gray-700"
          data-testid={DataTestIdEnum.PATIENT_SELECTION_LIST_SUBTITLE}
        >
          O agendamento deve ser feito em nome de quem será atendido.
        </Typography>
      </div>

      <div
        ref={scrollAreaRef}
        className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5"
        data-testid={DataTestIdEnum.PATIENT_SELECTION_SCROLL_AREA}
      >
        {warningMessage && (
          <div
            className="flex w-full shrink-0 items-center gap-3 rounded-2xl border border-warning-300 bg-warning-100 py-3 pl-3 pr-4 shadow-floating"
            data-testid={DataTestIdEnum.PATIENT_SELECTION_AGE_WARNING}
          >
            <Icon icon={faCircleExclamation} className="shrink-0 text-xl text-warning-600" />
            <p className="text-base font-semibold text-warning-700">{warningMessage}</p>
          </div>
        )}

        <div className="flex shrink-0 flex-col gap-4">
          {patients.map((patient, index) => (
            <Button
              key={`${patient.id}-${patient.documentNumber}`}
              disabled={!patient.isWithinAgeRange}
              variant="secondary"
              color="primary"
              className="flex flex-col gap-1 py-3 whitespace-normal break-words text-center"
              data-testid={`${DataTestIdEnum.PATIENT_SELECTION_OPTION}-${patient.type}-${index}`}
              onClick={() => handleSelectPatient(patient)}
            >
              <Typography variant={TypographyVariant.BODY_TEXT} className="font-semibold text-inherit text-lg">
                {patient.name}
              </Typography>
              <Typography variant={TypographyVariant.BODY_TEXT} className="text-inherit text-lg">
                {formatDocumentNumber(patient.documentNumber)}
              </Typography>
            </Button>
          ))}
        </div>
      </div>

      <div
        className={cn(
          'relative flex shrink-0 flex-col gap-5 bg-white px-5 pb-5 pt-4',
          listScrolls && 'shadow-modal',
        )}
        data-testid={DataTestIdEnum.PATIENT_SELECTION_ACTIONS}
      >
        <Button
          variant="outline"
          color="primary"
          data-testid={DataTestIdEnum.PATIENT_SELECTION_CREATE_BUTTON}
          onClick={() => handleJump({ key: PatientSelectionModalKeys.CREATE })}
        >
          Agendar para outra pessoa
        </Button>

        {offersAnotherOption && (
          <Button
            variant="text"
            color="primary"
            startIcon={faMagnifyingGlass}
            startIconSize="1x"
            data-testid={DataTestIdEnum.PATIENT_SELECTION_SEARCH_ANOTHER_BUTTON}
            onClick={onSearchAnotherOption}
          >
            Procurar outra opção
          </Button>
        )}
      </div>
    </div>
  );
}
